// Delivery Date Dashboard (views/purchase-department/DeliveryDateDashboard.vue): page, tables, filters.
// Display text only: codes, payloads and JTL comment text are never translated.
export default {
  en: {
    delivery: {
      common: {
        clickToCopy: 'Click to copy',
        daysShort: 'd',
        all: 'All',
        any: 'Any',
        optional: 'Optional',
        never: 'never',
        save: 'Save',
        apply: 'Apply',
        reject: 'Reject',
        undo: 'Undo',
        add: 'Add',
        cancel: 'Cancel',
        refresh: 'Refresh',
        yes: 'Yes',
        tbd: 'TBD',
      },
      cards: {
        paidUnpaid: '{paid} paid · {unpaid} unpaid',
        supportOverdue: {
          title: 'Overdue Support',
          who: 'Support · work on first',
          hint: 'Promised delivery date has already passed.',
        },
        definitelyLate: {
          title: 'Definitely Late',
          who: 'Support · second',
          hint: 'Promised date not passed yet, but the supplier delivery this order needs is dated later than the promised date. Overdue orders are not counted here.',
        },
        noDate: {
          title: 'No Date (Support)',
          who: 'Support · third',
          hint: 'Open orders without any promised delivery date — set one (see Suggested date).',
        },
        purchasingOverdue: {
          title: 'Overdue Purchasing',
          who: 'Purchasing only',
          hint: 'Supplier orders (LB) whose confirmed delivery date has passed without goods arriving.',
        },
        lbUnconfirmed: {
          title: 'LB Unconfirmed',
          who: 'Purchasing only',
          hint: 'Supplier orders without a confirmed date (JTL placeholder 31.12.2099) — chase the supplier.',
        },
        backorder: {
          title: 'Backorder ≥ 60 days',
          sub: '{warn} at 30–59 days · VOR has stock for {stock}',
          who: 'Purchasing only',
          hint: 'Items stuck on the VOR backorder supplier order — chase VOR or buy elsewhere.',
        },
      },
      tabs: {
        purchasing: 'Purchasing',
        support: 'Support',
        statistics: 'Statistics',
      },
      filters: {
        title: 'Search & Filters',
        subtitle: 'Filter by search, owner, created date, and traffic-light thresholds',
        search: 'Search (Order / LB No.)',
        searchPlaceholder: 'Search...',
        searchHint: 'Shipped/closed orders from the last 12 months are included when you search by number',
        owner: 'Owner',
        createdAfter: 'Created after',
        status: 'Status',
        noDateAge: 'No-date age (days)',
        options: 'Options',
        onlyMine: 'Only mine',
        clearAll: 'Clear all filters',
        platform: 'Platform',
        countryGroup: 'Country group',
        priority: 'Priority',
        payment: 'Payment',
      },
      legend: {
        title: 'Status legend',
        noDate: 'No date',
        overdue: 'Overdue (< today)',
        todayTomorrow: 'Today / tomorrow',
        thisWeek: 'This week',
        later: 'Later (> +7)',
        closed: 'Shipped / closed (search only)',
      },
      countryGroup: {
        germany: 'Germany',
        eu: 'EU (except Germany)',
        nonEu: 'Outside EU',
      },
      status: {
        overdue: 'Overdue',
        due_soon: 'Due soon',
        no_date: 'No date',
        definitely_late: 'Definitely late',
        later: 'Later (>7 days)',
        partial_delivery: 'Partial delivery',
        blocking: 'Blocks late order',
      },
      payment: {
        paid: 'Paid',
        partial: 'Partial',
        unpaid: 'Unpaid',
      },
      priority: {
        low: '{n} (low)',
        normal: '{n} (normal)',
        high: '{n} (high)',
        legacy: '{n} (legacy)',
      },
      reasons: {
        initial_date: 'Initial delivery date',
        supplier_delay: 'Supplier delay',
        customer_request: 'Customer request',
        partial_delivery: 'Partial delivery',
        not_ordered_yet: 'Not ordered yet',
        stock_error: 'Stock error',
        other: 'Other',
      },
      cols: {
        orderNo: 'Order No.',
        lbNo: 'LB No.',
        supplier: 'Supplier',
        orderDate: 'Order date',
        lbOrderDate: 'Order date',
        deliveryDate: 'Delivery date',
        positions: 'Positions',
        status: 'Status',
        due: 'Due',
        orderAmount: 'Order amount',
        lbOrderAmount: 'Order amount',
        buyer: 'Buyer',
        reference: 'Reference',
        notes: 'Notes',
        daysSinceOrder: 'Days since order',
        ageDays: 'Age (days)',
        reason: 'Reason',
        onHold: 'On hold',
        customer: 'Customer',
        source: 'Source',
        promisedDelivery: 'Promised delivery',
        incoming: 'Incoming',
        paid: 'Paid',
        paymentMethod: 'Payment method',
        owner: 'Owner',
        prio: 'Prio',
        suggestedDate: 'Suggested date',
        suggestion: 'Suggestion',
        since: 'Since',
        by: 'By',
        note: 'Note',
        sku: 'SKU',
        description: 'Description',
        qty: 'Qty',
        delivered: 'Delivered',
        leadTime: 'Lead time (d.)',
        deliveryTime: 'Delivery time',
        availability: 'Availability',
        unitPrice: 'Unit price (net, after disc.)',
        discount: 'Disc. %',
        lineTotal: 'Line total',
        item: 'Item',
        openQty: 'Open qty',
        link: 'Link',
        name: 'Name',
        team: 'Team',
        viaTool: 'Via tool',
        directJtl: 'Directly in JTL',
        total: 'Total',
        toolSharePct: 'Tool share %',
        holdChanges: 'Hold changes',
        otherToolActions: 'Other tool actions',
        lastToolUse: 'Last tool use',
        lastJtlChange: 'Last JTL change',
      },
      headerTitle: {
        incoming: 'latest supplier arrival across positions — hover for details',
        prio: 'JTL priority (-5 low … 5 high)',
      },
      states: {
        onHold: 'On hold',
        supplierOverdue: 'Supplier overdue',
        inStock: 'In stock',
        unconfirmed: 'Unconfirmed',
        notOrdered: 'Not ordered',
        partialDelivery: 'Partial delivery',
        needsPurchasing: 'Needs purchasing',
        needsPurchasingNothingOrdered: 'Needs purchasing — nothing ordered',
        orderedAwaiting: 'Ordered, awaiting confirmation',
      },
      tips: {
        needsPurchasing: 'An out-of-stock position has no dated incoming supplier order — purchasing must act first',
        supplierOverdue: 'The supplier\'s confirmed date has already passed and goods have not arrived',
        heldBack: 'Order is held back in JTL (Auftrag zurückhalten)',
        arrivalAfterPromise: 'Supplier arrival is dated after the promised delivery date',
        orderedUnconfirmed: 'The missing item is on an open supplier order, not yet confirmed by the supplier',
        definitelyLate: 'The supplier delivery this order needs is dated after the promised date',
        promisePassed: 'The promised date has passed',
        supplierArrivalOverdue: 'A supplier arrival this order waits on is already overdue — goods not arrived, new date unknown',
      },
      due: {
        in: 'in {n} d.',
        overdue: '{n} d. overdue',
        today: 'today',
      },
      bucket: {
        grey: 'No date',
        red: 'Overdue',
        orange: 'Today/Tomorrow',
        yellow: 'This week',
        green: 'Later',
      },
      closed: {
        credited: 'Credited',
        shipped: 'Shipped {date}',
        delivered: 'Delivered',
      },
      age: {
        tooltip: 'Order is {n} day old | Order is {n} days old',
        tooltipSince: 'Order is {n} day old (since {date}) | Order is {n} days old (since {date})',
      },
      backorder: {
        title: 'Backorder VOR {lb}',
        desc: 'Open items on VOR\'s backorder supplier order, oldest first. Yellow from 30 days, red from 60 days; green rows = VOR has the quantity in stock.',
        searchPlaceholder: 'Search article no., name or note...',
        empty: 'No open backorder items',
        addedTip: 'Approximate: positions have no created date, derived from neighbouring supplier orders',
        updated: 'updated {date}',
        vorHasStock: 'VOR has stock',
        vorPartial: 'VOR: {stock} of {qty}',
        vorStockOnly: 'Only items VOR has in stock',
        oldest: 'oldest {order}',
        loadError: 'Could not load the backorder list',
        exportCols: {
          name: 'Name',
          vorStockUpdated: 'VOR stock updated',
          oldestWaitingOrder: 'Oldest waiting order',
        },
        cols: {
          article: 'Article',
          openQty: 'Open qty',
          added: 'Added (approx.)',
          age: 'Age',
          vorStock: 'VOR stock',
          vorLeadTime: 'VOR delivery time',
          ownStock: 'Own stock',
          otherSuppliers: 'Other suppliers with stock',
          waitingOrders: 'Waiting customer orders',
          lbDate: 'LB date',
          note: 'Note',
        },
      },
      e4: {
        title: 'Customer orders waiting for purchasing',
        desc: 'Orders without a promised date whose missing item has no open supplier order (or only an overdue one). Open the order to see which positions are short.',
      },
      e1: {
        title: 'E1 – LB due soon / overdue',
        desc: 'Open supplier orders due within 7 days or overdue',
        posWithoutDate: '{n}/{total} without date',
        posShort: '{n} pos.',
        blocks: 'Blocks {order}',
        blocksTitle: 'Customer order {order} (promised {date}) depends on this LB and is or will be late',
        na: 'n/a',
        lateAction: 'LATE — action needed',
      },
      e2: {
        title: 'E2 – LB not confirmed',
        desc: 'Open supplier orders not yet confirmed by the supplier',
      },
      e3: {
        title: 'E3 – LB without date',
        desc: 'Open supplier orders without any delivery date',
      },
      s1: {
        title: 'S1 – Orders due soon / overdue',
        desc: 'Open customer orders due within 7 days or overdue (use the Status filter to add later-dated ones)',
      },
      s2: {
        title: 'S2 – Orders older than {days} days without date',
        desc: 'Open customer orders with no promised delivery date',
        fromStock: 'from stock',
        afterIncoming: 'after incoming',
      },
      popover: {
        title: 'Positions',
        error: 'Could not load positions',
        none: 'No article positions',
        matched: 'Matched by article — supplier orders are not reserved per customer order.',
        inStock: 'in stock',
        unconfirmed: 'unconfirmed · {lb}',
        overdueSince: 'overdue since {date} · {lb}',
        notOrdered: 'not ordered',
      },
      incomingHint: {
        incoming: 'incoming {date}',
        overdueSince: 'overdue since {date}',
        incomingTbd: 'incoming TBD',
      },
      partial: {
        shipNow: 'Ship available items now',
        offer: 'Offer partial delivery',
        label: '{action}: {ready} ready now, {delayed} delayed (latest arrival {latest})',
        labelNoAction: '{ready} ready now, {delayed} delayed (latest arrival {latest})',
        noSupplierDate: 'no supplier date',
      },
      auto: {
        on: 'Auto-apply on · suggestions (stock/incoming) are applied after {hours} h unless rejected · {pending} pending · {tail}',
        off: 'Auto-apply off (preview) · {pending} would be applied after {hours} h · {tail}',
        tail: '{skipped} skipped · {rejected} rejected · last run {lastRun}',
        hideReview: 'Hide skipped & rejected',
        showReview: 'Review skipped & rejected',
        cardTitle: 'Auto-apply: skipped & rejected',
        cardDesc: 'Suggestions the 24h auto-apply is not going to apply on its own',
        empty: 'Nothing skipped or rejected',
        applies: 'Auto applies {date}',
        wouldApply: 'Auto would apply {date}',
        skippedBadge: 'Auto-apply skipped · {reason}',
        rejectedBadge: 'Rejected · {reason}',
        skippedShort: 'Skipped · {reason}',
        modalPending: 'Auto-apply: pending until {date}',
        modalSkipped: 'Auto-apply: skipped · {reason}',
        modalRejected: 'Auto-apply: rejected · {reason}',
        skipReason: {
          on_hold: 'on hold',
          unpaid_prepayment: 'unpaid prepayment',
        },
        rejectedBy: 'by {name}',
        rejectedAt: 'at {date}',
      },
    },
  },
  de: {
    delivery: {
      common: {
        clickToCopy: 'Zum Kopieren klicken',
        daysShort: 'T.',
        all: 'Alle',
        any: 'Beliebig',
        optional: 'Optional',
        never: 'nie',
        save: 'Speichern',
        apply: 'Übernehmen',
        reject: 'Ablehnen',
        undo: 'Rückgängig',
        add: 'Hinzufügen',
        cancel: 'Abbrechen',
        refresh: 'Aktualisieren',
        yes: 'Ja',
        tbd: 'offen',
      },
      cards: {
        paidUnpaid: '{paid} bezahlt · {unpaid} unbezahlt',
        supportOverdue: {
          title: 'Überfällig (Support)',
          who: 'Support · zuerst bearbeiten',
          hint: 'Das zugesagte Lieferdatum ist bereits überschritten.',
        },
        definitelyLate: {
          title: 'Sicher verspätet',
          who: 'Support · als Zweites',
          hint: 'Zugesagtes Lieferdatum noch nicht erreicht, aber der für diesen Auftrag benötigte Zulauf ist später terminiert. Überfällige Aufträge sind hier nicht enthalten.',
        },
        noDate: {
          title: 'Ohne Datum (Support)',
          who: 'Support · als Drittes',
          hint: 'Offene Aufträge ohne zugesagtes Lieferdatum — Datum setzen (siehe Datumsvorschlag).',
        },
        purchasingOverdue: {
          title: 'Überfällig (Einkauf)',
          who: 'Nur Einkauf',
          hint: 'Lieferantenbestellungen (LB), deren bestätigter Liefertermin überschritten ist, ohne dass die Ware eingegangen ist.',
        },
        lbUnconfirmed: {
          title: 'LB unbestätigt',
          who: 'Nur Einkauf',
          hint: 'Lieferantenbestellungen ohne bestätigten Termin (JTL-Platzhalter 31.12.2099) — beim Lieferanten nachfassen.',
        },
        backorder: {
          title: 'Rückstand ≥ 60 Tage',
          sub: '{warn} bei 30–59 Tagen · VOR hat Bestand für {stock}',
          who: 'Nur Einkauf',
          hint: 'Artikel, die auf der VOR-Rückstandsbestellung hängen — bei VOR nachfassen oder woanders kaufen.',
        },
      },
      tabs: {
        purchasing: 'Einkauf',
        support: 'Support',
        statistics: 'Statistik',
      },
      filters: {
        title: 'Suche & Filter',
        subtitle: 'Filtern nach Suche, Zuständigem, Erstelldatum und Ampel-Schwellen',
        search: 'Suche (Auftrags-/LB-Nr.)',
        searchPlaceholder: 'Suchen...',
        searchHint: 'Bei Suche nach Nummer werden auch versendete/abgeschlossene Aufträge der letzten 12 Monate angezeigt',
        owner: 'Zuständig',
        createdAfter: 'Erstellt nach',
        status: 'Status',
        noDateAge: 'Ohne Datum seit (Tagen)',
        options: 'Optionen',
        onlyMine: 'Nur meine',
        clearAll: 'Alle Filter zurücksetzen',
        platform: 'Plattform',
        countryGroup: 'Ländergruppe',
        priority: 'Priorität',
        payment: 'Zahlungsstatus',
      },
      legend: {
        title: 'Status-Legende',
        noDate: 'Kein Datum',
        overdue: 'Überfällig (< heute)',
        todayTomorrow: 'Heute / morgen',
        thisWeek: 'Diese Woche',
        later: 'Später (> +7)',
        closed: 'Versendet / abgeschlossen (nur Suche)',
      },
      countryGroup: {
        germany: 'Deutschland',
        eu: 'EU (außer Deutschland)',
        nonEu: 'Außerhalb der EU',
      },
      status: {
        overdue: 'Überfällig',
        due_soon: 'Bald fällig',
        no_date: 'Kein Datum',
        definitely_late: 'Sicher verspätet',
        later: 'Später (>7 Tage)',
        partial_delivery: 'Teillieferung',
        blocking: 'Blockiert verspäteten Auftrag',
      },
      payment: {
        paid: 'Bezahlt',
        partial: 'Teilbezahlt',
        unpaid: 'Unbezahlt',
      },
      priority: {
        low: '{n} (niedrig)',
        normal: '{n} (normal)',
        high: '{n} (hoch)',
        legacy: '{n} (veraltet)',
      },
      reasons: {
        initial_date: 'Erstes Lieferdatum',
        supplier_delay: 'Lieferantenverzug',
        customer_request: 'Kundenwunsch',
        partial_delivery: 'Teillieferung',
        not_ordered_yet: 'Noch nicht bestellt',
        stock_error: 'Bestandsfehler',
        other: 'Sonstiges',
      },
      cols: {
        orderNo: 'Auftragsnr.',
        lbNo: 'LB-Nr.',
        supplier: 'Lieferant',
        orderDate: 'Auftragsdatum',
        lbOrderDate: 'Bestelldatum',
        deliveryDate: 'Lieferdatum',
        positions: 'Positionen',
        status: 'Status',
        due: 'Fällig',
        orderAmount: 'Auftragswert',
        lbOrderAmount: 'Bestellwert',
        buyer: 'Einkäufer',
        reference: 'Referenz',
        notes: 'Notizen',
        daysSinceOrder: 'Tage seit Bestellung',
        ageDays: 'Alter (Tage)',
        reason: 'Grund',
        onHold: 'Zurückgehalten',
        customer: 'Kunde',
        source: 'Quelle',
        promisedDelivery: 'Zugesagtes Lieferdatum',
        incoming: 'Zulauf',
        paid: 'Bezahlt',
        paymentMethod: 'Zahlungsart',
        owner: 'Zuständig',
        prio: 'Prio',
        suggestedDate: 'Datumsvorschlag',
        suggestion: 'Vorschlag',
        since: 'Seit',
        by: 'Von',
        note: 'Notiz',
        sku: 'Artikelnr.',
        description: 'Bezeichnung',
        qty: 'Menge',
        delivered: 'Geliefert',
        leadTime: 'Lieferzeit (T.)',
        deliveryTime: 'Lieferzeit',
        availability: 'Verfügbarkeit',
        unitPrice: 'Einzelpreis (netto, nach Rabatt)',
        discount: 'Rabatt %',
        lineTotal: 'Positionssumme',
        item: 'Artikel',
        openQty: 'Offene Menge',
        link: 'Zuordnung',
        name: 'Name',
        team: 'Team',
        viaTool: 'Über Tool',
        directJtl: 'Direkt in JTL',
        total: 'Gesamt',
        toolSharePct: 'Tool-Anteil %',
        holdChanges: 'Rückhalte-Änderungen',
        otherToolActions: 'Weitere Tool-Aktionen',
        lastToolUse: 'Letzte Tool-Nutzung',
        lastJtlChange: 'Letzte JTL-Änderung',
      },
      headerTitle: {
        incoming: 'spätester Zulauf über alle Positionen — Details per Mouseover',
        prio: 'JTL-Priorität (-5 niedrig … 5 hoch)',
      },
      states: {
        onHold: 'Zurückgehalten',
        supplierOverdue: 'Lieferant überfällig',
        inStock: 'Auf Lager',
        unconfirmed: 'Unbestätigt',
        notOrdered: 'Nicht bestellt',
        partialDelivery: 'Teillieferung',
        needsPurchasing: 'Einkauf nötig',
        needsPurchasingNothingOrdered: 'Einkauf nötig — nichts bestellt',
        orderedAwaiting: 'Bestellt, Bestätigung ausstehend',
      },
      tips: {
        needsPurchasing: 'Eine nicht vorrätige Position hat keine terminierte Lieferantenbestellung im Zulauf — der Einkauf muss zuerst handeln',
        supplierOverdue: 'Der bestätigte Liefertermin des Lieferanten ist überschritten, die Ware ist nicht eingegangen',
        heldBack: 'Auftrag ist in JTL zurückgehalten (Auftrag zurückhalten)',
        arrivalAfterPromise: 'Zulauf ist nach dem zugesagten Lieferdatum terminiert',
        orderedUnconfirmed: 'Der fehlende Artikel steht auf einer offenen Lieferantenbestellung, die der Lieferant noch nicht bestätigt hat',
        definitelyLate: 'Der für diesen Auftrag benötigte Zulauf ist nach dem zugesagten Lieferdatum terminiert',
        promisePassed: 'Das zugesagte Lieferdatum ist überschritten',
        supplierArrivalOverdue: 'Ein Zulauf, auf den dieser Auftrag wartet, ist überfällig — Ware nicht eingegangen, neuer Termin unbekannt',
      },
      due: {
        in: 'in {n} T.',
        overdue: '{n} T. überfällig',
        today: 'heute',
      },
      bucket: {
        grey: 'Kein Datum',
        red: 'Überfällig',
        orange: 'Heute/morgen',
        yellow: 'Diese Woche',
        green: 'Später',
      },
      closed: {
        credited: 'Gutgeschrieben',
        shipped: 'Versendet {date}',
        delivered: 'Geliefert',
      },
      age: {
        tooltip: 'Auftrag ist {n} Tag alt | Auftrag ist {n} Tage alt',
        tooltipSince: 'Auftrag ist {n} Tag alt (seit {date}) | Auftrag ist {n} Tage alt (seit {date})',
      },
      backorder: {
        title: 'Rückstand VOR {lb}',
        desc: 'Offene Positionen der VOR-Rückstandsbestellung, älteste zuerst. Gelb ab 30 Tagen, rot ab 60 Tagen; grüne Zeilen = VOR hat die Menge auf Lager.',
        searchPlaceholder: 'Artikelnr., Name oder Hinweis suchen...',
        empty: 'Keine offenen Rückstandspositionen',
        addedTip: 'Ungefähr: Positionen haben kein Erstelldatum, abgeleitet aus benachbarten Lieferantenbestellungen',
        updated: 'aktualisiert {date}',
        vorHasStock: 'VOR hat Bestand',
        vorPartial: 'VOR: {stock} von {qty}',
        vorStockOnly: 'Nur Artikel, die VOR auf Lager hat',
        oldest: 'ältester {order}',
        loadError: 'Rückstandsliste konnte nicht geladen werden',
        exportCols: {
          name: 'Name',
          vorStockUpdated: 'VOR-Bestand aktualisiert',
          oldestWaitingOrder: 'Ältester wartender Auftrag',
        },
        cols: {
          article: 'Artikel',
          openQty: 'Offene Menge',
          added: 'Hinzugefügt (ca.)',
          age: 'Alter',
          vorStock: 'VOR-Bestand',
          vorLeadTime: 'VOR-Lieferzeit',
          ownStock: 'Eigener Bestand',
          otherSuppliers: 'Andere Lieferanten mit Bestand',
          waitingOrders: 'Wartende Kundenaufträge',
          lbDate: 'LB-Datum',
          note: 'Hinweis',
        },
      },
      e4: {
        title: 'Kundenaufträge, die auf den Einkauf warten',
        desc: 'Aufträge ohne zugesagtes Lieferdatum, deren fehlender Artikel keine offene Lieferantenbestellung hat (oder nur eine überfällige). Auftrag öffnen, um die fehlenden Positionen zu sehen.',
      },
      e1: {
        title: 'E1 – LB bald fällig / überfällig',
        desc: 'Offene Lieferantenbestellungen, die innerhalb von 7 Tagen fällig oder überfällig sind',
        posWithoutDate: '{n}/{total} ohne Datum',
        posShort: '{n} Pos.',
        blocks: 'Blockiert {order}',
        blocksTitle: 'Kundenauftrag {order} (zugesagt {date}) hängt von dieser LB ab und ist oder wird verspätet',
        na: 'k. A.',
        lateAction: 'VERSPÄTET — Handlungsbedarf',
      },
      e2: {
        title: 'E2 – LB nicht bestätigt',
        desc: 'Offene Lieferantenbestellungen, die der Lieferant noch nicht bestätigt hat',
      },
      e3: {
        title: 'E3 – LB ohne Datum',
        desc: 'Offene Lieferantenbestellungen ohne Lieferdatum',
      },
      s1: {
        title: 'S1 – Aufträge bald fällig / überfällig',
        desc: 'Offene Kundenaufträge, die innerhalb von 7 Tagen fällig oder überfällig sind (über den Status-Filter lassen sich spätere ergänzen)',
      },
      s2: {
        title: 'S2 – Aufträge älter als {days} Tage ohne Datum',
        desc: 'Offene Kundenaufträge ohne zugesagtes Lieferdatum',
        fromStock: 'ab Lager',
        afterIncoming: 'nach Zulauf',
      },
      popover: {
        title: 'Positionen',
        error: 'Positionen konnten nicht geladen werden',
        none: 'Keine Artikelpositionen',
        matched: 'Zuordnung per Artikel — Lieferantenbestellungen sind nicht je Kundenauftrag reserviert.',
        inStock: 'auf Lager',
        unconfirmed: 'unbestätigt · {lb}',
        overdueSince: 'überfällig seit {date} · {lb}',
        notOrdered: 'nicht bestellt',
      },
      incomingHint: {
        incoming: 'Zulauf {date}',
        overdueSince: 'überfällig seit {date}',
        incomingTbd: 'Zulauf offen',
      },
      partial: {
        shipNow: 'Verfügbare Artikel jetzt versenden',
        offer: 'Teillieferung anbieten',
        label: '{action}: {ready} jetzt lieferbar, {delayed} verzögert (späteste Lieferung {latest})',
        labelNoAction: '{ready} jetzt lieferbar, {delayed} verzögert (späteste Lieferung {latest})',
        noSupplierDate: 'kein Lieferantentermin',
      },
      auto: {
        on: 'Auto-Übernahme an · Vorschläge (Lager/Zulauf) werden nach {hours} h übernommen, sofern nicht abgelehnt · {pending} ausstehend · {tail}',
        off: 'Auto-Übernahme aus (Vorschau) · {pending} würden nach {hours} h übernommen · {tail}',
        tail: '{skipped} übersprungen · {rejected} abgelehnt · letzter Lauf {lastRun}',
        hideReview: 'Übersprungene & abgelehnte ausblenden',
        showReview: 'Übersprungene & abgelehnte prüfen',
        cardTitle: 'Auto-Übernahme: übersprungen & abgelehnt',
        cardDesc: 'Vorschläge, die die 24-h-Auto-Übernahme nicht von selbst übernimmt',
        empty: 'Nichts übersprungen oder abgelehnt',
        applies: 'Wird am {date} automatisch übernommen',
        wouldApply: 'Würde am {date} automatisch übernommen',
        skippedBadge: 'Auto-Übernahme übersprungen · {reason}',
        rejectedBadge: 'Abgelehnt · {reason}',
        skippedShort: 'Übersprungen · {reason}',
        modalPending: 'Auto-Übernahme: ausstehend bis {date}',
        modalSkipped: 'Auto-Übernahme: übersprungen · {reason}',
        modalRejected: 'Auto-Übernahme: abgelehnt · {reason}',
        skipReason: {
          on_hold: 'zurückgehalten',
          unpaid_prepayment: 'unbezahlte Vorkasse',
        },
        rejectedBy: 'von {name}',
        rejectedAt: 'am {date}',
      },
    },
  },
}
